import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { committeeService } from '../../services/committeeService';
import { userService } from '../../services/userService';
import Button from '../ui/Button';
import Input from '../ui/Input';
import LoadingSpinner from '../common/LoadingSpinner';
import ConfirmDialog from '../ui/ConfirmDialog';
import { COMMITTEE_SUCCESS, COMMITTEE_ERRORS } from '../../constants/committeeConstants';

const CommitteeManagementModal = ({ isOpen, onClose }) => {
  const [committees, setCommittees] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedCommittee, setSelectedCommittee] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    members: []
  });
  const [errors, setErrors] = useState({});
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteCommitteeId, setDeleteCommitteeId] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    if (isOpen) {
      loadData();
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [committeesData, usersData] = await Promise.all([
        committeeService.getAllCommittees(),
        userService.getUsersWithCommittees()
      ]);
      setCommittees(committeesData);
      setUsers(usersData);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateNew = () => {
    setSelectedCommittee(null);
    setFormData({ name: '', members: [] });
    setErrors({});
    setShowForm(true);
  };

  const handleEdit = (committee) => {
    setSelectedCommittee(committee);
    setFormData({
      name: committee.name,
      members: committee.members || []
    });
    setErrors({});
    setShowForm(true);
  };

  const handleDelete = (committeeId) => {
    setDeleteCommitteeId(committeeId);
    setShowDeleteDialog(true);
  };

  const confirmDelete = async () => {
    try {
      setLoading(true);
      await committeeService.deleteCommittee(deleteCommitteeId);
      setSuccessMessage(COMMITTEE_SUCCESS.DELETED);
      setTimeout(() => setSuccessMessage(''), 3000);
      await loadData();
      setShowDeleteDialog(false);
      setDeleteCommitteeId(null);
    } catch (error) {
      console.error('Error deleting committee:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate
    const validation = committeeService.validateCommitteeData(formData);
    if (!validation.isValid) {
      setErrors(validation.errors);
      return;
    }

    // Check name uniqueness
    const nameExists = await committeeService.isCommitteeNameExists(
      formData.name, 
      selectedCommittee?.id
    );
    if (nameExists) {
      setErrors({ name: COMMITTEE_ERRORS.NAME_EXISTS });
      return;
    }

    try {
      setLoading(true);
      
      if (selectedCommittee) {
        await committeeService.updateCommittee(selectedCommittee.id, formData);
        setSuccessMessage(COMMITTEE_SUCCESS.UPDATED);
      } else {
        await committeeService.createCommittee(formData);
        setSuccessMessage(COMMITTEE_SUCCESS.CREATED);
      }
      
      setTimeout(() => setSuccessMessage(''), 3000);
      await loadData();
      setShowForm(false);
    } catch (error) {
      console.error('Error saving committee:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUserToggle = (userId) => {
    setFormData(prev => ({
      ...prev,
      members: prev.members.includes(userId)
        ? prev.members.filter(id => id !== userId)
        : [...prev.members, userId]
    }));
  };

  const getUserName = (user) => {
    return user.fullName || 'Unknown User';
  };

  if (!isOpen) return null;

  const modalContent = (
    <div 
      className="fixed inset-0 bg-bg-overlay flex items-center justify-center p-4"
      style={{ zIndex: 99999 }}
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div 
        className="bg-surface-elevated w-full max-w-4xl max-h-[90vh] rounded-xl shadow-theme-lg border border-border-primary overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border-primary bg-surface-elevated">
          <h2 className="text-xl font-semibold text-text-primary">Committee Management</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-surface-hover rounded-lg transition-colors"
          >
            <svg className="w-5 h-5 text-text-secondary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto bg-surface-elevated" style={{ maxHeight: 'calc(90vh - 140px)' }}>
          {/* Success Message */}
          {successMessage && (
            <div className="mb-4 bg-success-subtle border border-success rounded-lg p-3">
              <div className="flex items-center">
                <svg className="w-4 h-4 text-success mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <p className="text-sm text-success font-medium">{successMessage}</p>
              </div>
            </div>
          )}

          {/* Loading */}
          {loading && (
            <div className="flex justify-center py-8">
              <LoadingSpinner size="lg" />
            </div>
          )}

          {/* Committee Form */}
          {showForm && !loading && (
            <div>
              <h3 className="text-lg font-medium text-text-primary mb-4">
                {selectedCommittee ? 'Edit Committee' : 'Create New Committee'}
              </h3>
              
              <form onSubmit={handleSubmit} className="space-y-4">
                <Input
                  label="Committee Name"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  error={errors.name}
                  placeholder="Enter committee name"
                  required
                />

                <div>
                  <label className="block text-sm font-medium text-text-primary mb-2">
                    Committee Members
                  </label>
                  {errors.members && (
                    <p className="text-sm text-error mb-2">{errors.members}</p>
                  )}
                  
                  <div className="max-h-48 overflow-y-auto border border-border-primary rounded-lg bg-surface-primary">
                    {users.map(user => (
                      <label key={user.id} className="flex items-center p-3 hover:bg-surface-hover cursor-pointer border-b border-border-primary last:border-b-0">
                        <input
                          type="checkbox"
                          checked={formData.members.includes(user.id)}
                          onChange={() => handleUserToggle(user.id)}
                          className="mr-3 rounded border-border-primary focus:ring-accent focus:border-accent"
                        />
                        <div className="flex-1">
                          <p className="text-sm font-medium text-text-primary">
                            {getUserName(user)}
                          </p>
                          <p className="text-xs text-text-secondary">
                            {user.email}
                          </p>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>

                <div className="flex space-x-3">
                  <Button type="submit" disabled={loading} size="md">
                    {selectedCommittee ? 'Update Committee' : 'Create Committee'}
                  </Button>
                  <Button 
                    type="button" 
                    variant="outline" 
                    onClick={() => setShowForm(false)}
                    size="md"
                  >
                    Cancel
                  </Button>
                </div>
              </form>
            </div>
          )}

          {/* Committee List */}
          {!showForm && !loading && (
            <div>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-text-primary">Existing Committees</h3>
                <Button onClick={handleCreateNew} size="md">
                  Create New Committee
                </Button>
              </div>

              {committees.length === 0 ? (
                <div className="text-center py-8">
                  <div className="mb-4">
                    <svg className="w-16 h-16 text-text-quaternary mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M17 20h5v-2a3 3 0 00-5.196-2.121M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.196-2.121M7 20v-2c0-.656.126-1.283.356-1.857M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                    </svg>
                  </div>
                  <h4 className="text-lg font-medium text-text-primary mb-2">No committees created yet</h4>
                  <p className="text-text-secondary mb-4">Create your first committee to start organizing users into groups.</p>
                  <Button onClick={handleCreateNew} size="md">
                    Create First Committee
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  {committees.map(committee => (
                    <div key={committee.id} className="border border-border-primary rounded-lg p-4 bg-surface-primary">
                      <div className="flex items-center justify-between">
                        <div>
                          <h4 className="font-medium text-text-primary">{committee.name}</h4>
                          <p className="text-sm text-text-secondary">
                            {committee.members?.length || 0} member{committee.members?.length !== 1 ? 's' : ''}
                          </p>
                        </div>
                        <div className="flex space-x-2">
                          <button
                            onClick={() => handleEdit(committee)}
                            className="p-2 text-accent hover:bg-accent-subtle rounded-lg transition-colors"
                            title="Edit Committee"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                          </button>
                          <button
                            onClick={() => handleDelete(committee.id)}
                            className="p-2 text-error hover:bg-error-subtle rounded-lg transition-colors"
                            title="Delete Committee"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        </div>
                      </div>
                      
                      {committee.members && committee.members.length > 0 && (
                        <div className="mt-3 pt-3 border-t border-border-primary">
                          <p className="text-xs font-medium text-text-secondary mb-2">Members:</p>
                          <div className="flex flex-wrap gap-2">
                            {committee.members.map(memberId => {
                              const user = users.find(u => u.id === memberId);
                              return user ? (
                                <span key={memberId} className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-accent-subtle text-accent">
                                  {getUserName(user)}
                                </span>
                              ) : null;
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Delete Confirmation Dialog */}
        <ConfirmDialog
          isOpen={showDeleteDialog}
          onClose={() => setShowDeleteDialog(false)}
          onConfirm={confirmDelete}
          title="Delete Committee"
          message="Are you sure you want to delete this committee? This action cannot be undone."
          confirmText="Delete"
          cancelText="Cancel"
          type="danger"
          loading={loading}
        />
      </div>
    </div>
  );

  // Use portal to render at document.body level
  return createPortal(modalContent, document.body);
};

export default CommitteeManagementModal;